import React, { useState, useEffect } from 'react';
import { Button, Col, Row, Form as BootstrapForm } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { WebApiCalls } from '../../webApiCalls/WebApiCalls';
import { formatDate } from '../../helpers/utils';
import Select from 'react-select';
import decryptData from '../../helpers/Decryption';

// Replace offerTypeOptions with the new one
const offerTypes = ['Signup Reward', 'Disruption Reward', 'Reward for All TR Members', 'Special Reward'];
const offerTypeOptions = offerTypes.map((type) => ({ value: type, label: type }));

const AddEditOffer = () => {
    const { state } = useLocation();
    const [status, setStatus] = useState('active');
    const navigate = useNavigate();
    const { getToken } = useAuth();
    const [offerDetails, setOfferDetails] = useState(null);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [allBranchesSelected, setAllBranchesSelected] = useState(false);
    const [menuItems, setMenuItems] = useState([]);

    const objectIdRegex = /^[a-f\d]{24}$/i;

    // Update validation schema to conditionally require start/end date and time range
    const validate = Yup.object({
        name: Yup.string().required('Required'),
        offerType: Yup.string().required('Reward type is required'),
        startDate: Yup.string().test('is-required', 'Required', function (value) {
            return this.parent.offerType !== 'Disruption Reward' ? !!value : true;
        }),
        endDate: Yup.string().test('is-required', 'Required', function (value) {
            return this.parent.offerType !== 'Disruption Reward' ? !!value : true;
        }),
        timeRangeStart: Yup.string().test('is-required', 'Required', function (value) {
            return this.parent.offerType !== 'Disruption Reward' ? !!value : true;
        }),
        timeRangeEnd: Yup.string().test('is-required', 'Required', function (value) {
            return this.parent.offerType !== 'Disruption Reward' ? !!value : true;
        }),
        menuItem: Yup.string().required('Menu item is required').matches(objectIdRegex, 'Menu item must be a valid identifier'),
        branches: Yup.array()
            .transform((value) => {
                if (!Array.isArray(value)) return [];
                return value
                    .flat()
                    .map((v) => {
                        if (typeof v === 'string') return v;
                        if (v && typeof v === 'object') {
                            if (v._id && typeof v._id === 'string') return v._id;
                            if (v.value && typeof v.value === 'string') return v.value;
                        }
                        return '';
                    })
                    .filter((v) => typeof v === 'string' && objectIdRegex.test(v));
            })
            .of(Yup.string().matches(objectIdRegex, 'Branch must be a valid identifier'))
            .min(1, 'At least one branch is required')
    });

    useEffect(() => {
        // Persist state to local storage if available
        if (state) {
            localStorage.setItem('offerState', JSON.stringify(state));
        }
    }, [state]);

    useEffect(() => {
        // Retrieve state from local storage if not available in useLocation
        const savedState = localStorage.getItem('offerState');
        const parsedState = savedState ? JSON.parse(savedState) : null;

        if (state?.offer_id && state?.offer_detail) {
            setOfferDetails(state.offer_detail);
            setStatus(state.offer_detail.status);
        } else if (parsedState?.offer_id && parsedState?.offer_detail) {
            setOfferDetails(parsedState.offer_detail);
            setStatus(parsedState.offer_detail.status);
        }
    }, [state]);

    useEffect(() => {
        const fetchBranches = async (pageSize = 10, pageNo = 0, status = '', search = '') => {
            const token = getToken();
            if (token) {
                setLoading(true);
                const res = await new WebApiCalls().getAllBranches(token, pageSize, pageNo, status, search);
                if (res?.code === 200) {
                    const decryptedData = await decryptData(res?.data);
                    setBranches(decryptedData);
                }
                setLoading(false);
            }
        };

        fetchBranches();
    }, [getToken]);

    useEffect(() => {
        // Fetch menu items for vendor (for selection)
        const fetchMenuItems = async () => {
            const token = getToken();
            if (token) {
                const res = await new WebApiCalls().getMenuItems(token, 100, 1, '', 'active');
                if (res?.code === 200) {
                    const decryptedData = await decryptData(res?.data);
                    setMenuItems(decryptedData || []);
                }
            }
        };
        fetchMenuItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getToken]);

    useEffect(() => {
        return () => {
            // Clear local storage state when navigating away
            localStorage.removeItem('offerState');
        };
    }, []);

    const branchOptions = [
        { value: 'ALL_BRANCHES', label: 'All Branches' },
        ...branches.map((branch) => ({
            value: branch._id,
            label: branch.name
        }))
    ];

    const menuItemOptions = menuItems.map((item) => ({
        value: item._id,
        label: item.itemName
    }));

    const handleBranchChange = (selectedOptions, formik) => {
        let newBranches = [];
        let allSelected = false;
        if (!selectedOptions) {
            newBranches = [];
        } else {
            const values = selectedOptions.map((option) => option.value);
            if (values.includes('ALL_BRANCHES')) {
                newBranches = branches.map((b) => b._id);
                allSelected = true;
            } else {
                newBranches = values;
            }
        }
        formik.setFieldValue('branches', newBranches);
        setAllBranchesSelected(allSelected);
    };

    return (
        <Row className="d-flex justify-content-center">
            <Col xs={12} sm={12} md={11} lg={11} className="shadow-lg p-5 m-3">
                <Formik
                    initialValues={{
                        name: offerDetails?.name || '',
                        startDate: offerDetails?.startDate ? formatDate(offerDetails.startDate) : '',
                        endDate: offerDetails?.endDate ? formatDate(offerDetails.endDate) : '',
                        timeRangeStart: offerDetails?.timeRange?.start || '00:00',
                        timeRangeEnd: offerDetails?.timeRange?.end || '00:00',
                        menuItem: (typeof offerDetails?.menuItem === 'object' ? offerDetails?.menuItem?._id : offerDetails?.menuItem) || '',
                        offerType: offerDetails?.offerType || '',
                        branches: offerDetails?.branches ? offerDetails.branches.map((b) => (typeof b === 'object' ? b._id : b)) : []
                    }}
                    validationSchema={validate}
                    onSubmit={async (values) => {
                        const token = getToken();
                        if (!token) return;
                        const branchesToSend = allBranchesSelected ? branches.map((b) => b._id) : values.branches;
                        // Remove startDate, endDate, and timeRange for Disruption Reward
                        let payload = {
                            ...values,
                            status,
                            branches: branchesToSend,
                            menuItem: values.menuItem,
                            offerType: values.offerType,
                            isDisruptionOffer: values.offerType === 'Disruption Reward'
                        };
                        if (values.offerType !== 'Disruption Reward') {
                            payload.timeRange = {
                                start: values.timeRangeStart,
                                end: values.timeRangeEnd
                            };
                            payload.startDate = values.startDate;
                            payload.endDate = values.endDate;
                        }
                        const apiCall = state?.offer_id ? new WebApiCalls().updateOffer(token, state?.offer_id, payload) : new WebApiCalls().createOffer(token, payload);
                        const res = await apiCall;
                        if (res?.code === 200) {
                            toast.success(`Reward ${state?.offer_id ? 'updated' : 'created'} successfully.`);
                            navigate(state.redirect_url, {
                                state: {
                                    redirect_url: '/rewards'
                                }
                            });
                        } else {
                            if (res?.desc) {
                                toast.error(res?.desc);
                            } else {
                                toast.error(`Failed to ${state?.offer_id ? 'update' : 'create'} offer.`);
                            }
                        }
                    }}
                    enableReinitialize={true}
                >
                    {(formik) => (
                        <Form>
                            <Row>
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                    <BootstrapForm.Group>
                                        <BootstrapForm.Label>Reward Name</BootstrapForm.Label>
                                        <BootstrapForm.Control type="text" name="name" placeholder="Enter offer name" {...formik.getFieldProps('name')} />
                                        {formik.errors.name && <div className="text-danger">{formik.errors.name}</div>}
                                    </BootstrapForm.Group>
                                </Col>
                                {/* Move Reward Type Dropdown here, after Reward Name */}
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                    <BootstrapForm.Group>
                                        <BootstrapForm.Label>Reward Type</BootstrapForm.Label>
                                        <Select
                                            options={offerTypeOptions}
                                            value={offerTypeOptions.find((option) => option.value === formik.values.offerType) || null}
                                            onChange={(option) => formik.setFieldValue('offerType', option ? option.value : '')}
                                            placeholder="Select offer type"
                                            isClearable
                                            name="offerType"
                                        />
                                        {formik.touched.offerType && formik.errors.offerType && <div className="text-danger">{formik.errors.offerType}</div>}
                                    </BootstrapForm.Group>
                                </Col>
                                {/* Menu Item Single Select */}
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                    <BootstrapForm.Group>
                                        <BootstrapForm.Label>Menu Item</BootstrapForm.Label>
                                        <div>
                                            <Select
                                                options={menuItemOptions}
                                                value={menuItemOptions.find((option) => option.value === formik.values.menuItem) || null}
                                                onChange={(option) => formik.setFieldValue('menuItem', option ? option.value : '')}
                                                placeholder="Select menu item"
                                                isClearable
                                                name="menuItem"
                                            />
                                            {formik.touched.menuItem && formik.errors.menuItem && <div className="text-danger">{formik.errors.menuItem}</div>}
                                        </div>
                                    </BootstrapForm.Group>
                                </Col>
                                <Col xs={12} sm={12} md={6} lg={6}>
                                    <BootstrapForm.Group>
                                        <BootstrapForm.Label>Branches</BootstrapForm.Label>
                                        <div className="multiple-selector">
                                            <Select
                                                options={branchOptions}
                                                isMulti
                                                isLoading={loading}
                                                value={
                                                    allBranchesSelected
                                                        ? [branchOptions[0]]
                                                        : branchOptions.filter((option) => formik.values.branches.includes(option.value))
                                                }
                                                onChange={(selectedOptions) => handleBranchChange(selectedOptions, formik)}
                                                closeMenuOnSelect={false}
                                            />
                                        </div>
                                    </BootstrapForm.Group>
                                </Col>
                                {/* Only show Start/End Date and Time Range if not Disruption Reward */}
                                {!formik.values.offerType || formik.values.offerType !== 'Disruption Reward' ? (
                                    <>
                                        <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                            <BootstrapForm.Group>
                                                <BootstrapForm.Label>Start Date</BootstrapForm.Label>
                                                <BootstrapForm.Control type="date" name="startDate" {...formik.getFieldProps('startDate')} />
                                                {formik.errors.startDate && <div className="text-danger">{formik.errors.startDate}</div>}
                                            </BootstrapForm.Group>
                                        </Col>
                                        <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                            <BootstrapForm.Group>
                                                <BootstrapForm.Label>End Date</BootstrapForm.Label>
                                                <BootstrapForm.Control type="date" name="endDate" {...formik.getFieldProps('endDate')} />
                                                {formik.errors.endDate && <div className="text-danger">{formik.errors.endDate}</div>}
                                            </BootstrapForm.Group>
                                        </Col>
                                        <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                            <BootstrapForm.Group>
                                                <BootstrapForm.Label>Time Range Start</BootstrapForm.Label>
                                                <BootstrapForm.Control type="time" name="timeRangeStart" {...formik.getFieldProps('timeRangeStart')} />
                                                {formik.errors.timeRangeStart && <div className="text-danger">{formik.errors.timeRangeStart}</div>}
                                            </BootstrapForm.Group>
                                        </Col>
                                        <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                            <BootstrapForm.Group>
                                                <BootstrapForm.Label>Time Range End</BootstrapForm.Label>
                                                <BootstrapForm.Control type="time" name="timeRangeEnd" {...formik.getFieldProps('timeRangeEnd')} />
                                                {formik.errors.timeRangeEnd && <div className="text-danger">{formik.errors.timeRangeEnd}</div>}
                                            </BootstrapForm.Group>
                                        </Col>
                                    </>
                                ) : null}
                            </Row>
                            <div className="text-end">
                                <div
                                    onClick={() =>
                                        navigate(state.redirect_url, {
                                            state: {
                                                redirect_url: '/rewards'
                                            }
                                        })
                                    }
                                    className="d-inline-block me-3"
                                >
                                    <Button type="button" className="theme-cancel-btn me-3">
                                        Cancel
                                    </Button>
                                </div>
                                <Button type="submit" className="theme-btn">
                                    {state?.offer_id ? 'Update' : 'Save'}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </Col>
        </Row>
    );
};

export default AddEditOffer;
